import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShoppingCart, Loader2, Receipt, Minus, Plus, XCircle, CheckCircle2, Clock, Link2, Copy } from "lucide-react";
import { authHeaders, BESTELL_STATUS, PREIS_PRO_PLATZ, fmtDate, fmtEur, formatApiErrorDetail } from "@/components/schulungen/roles";
import { trackConversion } from "@/lib/adsConversion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function StatusBadge({ map, status }) {
  const s = map[status] || { label: status, cls: "bg-slate-100 text-slate-600 border-slate-200" };
  return <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold border ${s.cls}`} data-testid={`status-${status}`}>{s.label}</span>;
}

function BestellDialog({ open, onOpenChange, onCreated }) {
  const { user } = useAuth();
  const [anzahl, setAnzahl] = useState(5);
  const [bemerkung, setBemerkung] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/schulungen/bestellungen`, { anzahl: Number(anzahl), bemerkung }, authHeaders(user));
      trackConversion("platzbestellung", { value: data.gesamt, transactionId: data.id });
      onCreated(data);
      onOpenChange(false);
      setAnzahl(5); setBemerkung("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail, "Bestellung fehlgeschlagen"));
    }
    setLoading(false);
  };

  const n = Math.max(1, Math.min(500, Number(anzahl) || 1));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md rounded-2xl" data-testid="bestell-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Manrope']">Schulungsplätze bestellen</DialogTitle>
          <DialogDescription>{fmtEur(PREIS_PRO_PLATZ)} pro Platz · Zahlung auf Rechnung · Plätze sofort verfügbar · Zertifikate nach Zahlungseingang</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          <div>
            <Label className="text-sm font-semibold text-slate-700 mb-2 block">Anzahl Plätze</Label>
            <div className="flex items-center gap-2">
              <Button type="button" variant="outline" className="h-12 w-12 rounded-xl" onClick={() => setAnzahl(Math.max(1, n - 1))} data-testid="anzahl-minus"><Minus className="h-4 w-4" /></Button>
              <Input type="number" min={1} max={500} value={anzahl} onChange={(e) => setAnzahl(e.target.value)} className="h-12 rounded-xl text-center text-lg font-bold" data-testid="anzahl-input" />
              <Button type="button" variant="outline" className="h-12 w-12 rounded-xl" onClick={() => setAnzahl(Math.min(500, n + 1))} data-testid="anzahl-plus"><Plus className="h-4 w-4" /></Button>
            </div>
          </div>
          <div>
            <Label className="text-sm font-semibold text-slate-700 mb-2 block">Bemerkung (optional, z. B. Bestellnummer / Kostenstelle)</Label>
            <Textarea value={bemerkung} onChange={(e) => setBemerkung(e.target.value)} className="rounded-xl" rows={2} data-testid="bemerkung-input" />
          </div>
          <div className="bg-slate-50 rounded-xl p-4 flex items-center justify-between">
            <span className="text-sm text-slate-600">{n} × {fmtEur(PREIS_PRO_PLATZ)}</span>
            <span className="font-['Manrope'] text-2xl font-bold text-slate-900" data-testid="bestell-summe">{fmtEur(n * PREIS_PRO_PLATZ)}</span>
          </div>
          <Button type="submit" disabled={loading} className="w-full bg-cyan-700 hover:bg-cyan-600 text-white rounded-xl h-12 font-semibold" data-testid="bestell-submit">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verbindlich auf Rechnung bestellen"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function BestellBestaetigung({ bestellung, onClose }) {
  if (!bestellung) return null;
  const steps = [
    { icon: CheckCircle2, title: "Bestellung eingegangen – Plätze sofort verfügbar", desc: `${bestellung.anzahl} Schulungsplätze · ${fmtEur(bestellung.gesamt)}. Sie können ab sofort Mitarbeitende einladen. Eine Bestätigung wurde Ihnen per E-Mail gesendet.`, state: "done" },
    { icon: Link2, title: "Einladungslinks erzeugen", desc: "Im Tab „Mitarbeitende“ legen Sie pro Person einen Einladungslink an.", state: "next" },
    { icon: Copy, title: "Link selbst weiterleiten", desc: "Kopieren Sie den Link und senden Sie ihn Ihren Mitarbeitenden selbst zu – ein automatischer Versand erfolgt nicht.", state: "later" },
    { icon: Clock, title: "Rechnung begleichen – Zertifikate per Post", desc: "Die Rechnung senden wir Ihnen zu. Zertifikate werden erst nach Zahlungseingang ausgestellt und per Post an Ihre Firmenadresse versendet.", state: "later" },
  ];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg rounded-2xl" data-testid="bestell-bestaetigung">
        <DialogHeader>
          <DialogTitle className="font-['Manrope'] flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-green-600" />Vielen Dank für Ihre Bestellung</DialogTitle>
          <DialogDescription>So geht es jetzt weiter – Ihre Plätze sind bereits freigeschaltet.</DialogDescription>
        </DialogHeader>
        <ol className="space-y-3">
          {steps.map((st, i) => (
            <li key={st.title} className={`flex gap-3 p-3 rounded-xl border ${st.state === "done" ? "bg-green-50 border-green-200" : st.state === "next" ? "bg-amber-50 border-amber-200" : "bg-slate-50 border-slate-200"}`}>
              <div className={`h-9 w-9 shrink-0 rounded-lg flex items-center justify-center ${st.state === "done" ? "bg-green-600 text-white" : st.state === "next" ? "bg-amber-500 text-white" : "bg-white border border-slate-200 text-slate-500"}`}><st.icon className="h-4 w-4" /></div>
              <div><p className="text-sm font-semibold text-slate-800">{i + 1}. {st.title}</p><p className="text-xs text-slate-600 mt-0.5">{st.desc}</p></div>
            </li>
          ))}
        </ol>
        <Button onClick={onClose} className="w-full bg-cyan-700 hover:bg-cyan-600 text-white rounded-xl h-11 font-semibold" data-testid="bestell-bestaetigung-ok">Verstanden</Button>
      </DialogContent>
    </Dialog>
  );
}

export default function FirmenBestellungen({ onChanged }) {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [bestaetigung, setBestaetigung] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/schulungen/bestellungen`, authHeaders(user)).then((r) => setItems(r.data)).catch(() => toast.error("Bestellungen konnten nicht geladen werden"));
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const storno = async (b) => {
    if (!window.confirm(`Bestellung über ${b.anzahl} Plätze stornieren?`)) return;
    try {
      await axios.patch(`${API}/schulungen/bestellungen/${b.id}/storno`, {}, authHeaders(user));
      toast.success("Bestellung storniert");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5" data-testid="firmen-bestellungen">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-['Manrope'] text-xl font-bold text-slate-900">Bestellungen</h2>
          <p className="text-sm text-slate-500">Plätze stehen sofort nach der Bestellung zur Verfügung. Zertifikate werden erst nach Zahlungseingang ausgestellt und per Post versendet.</p>
        </div>
        <Button onClick={() => setOpen(true)} className="bg-cyan-700 hover:bg-cyan-600 text-white rounded-xl h-11 px-5 font-semibold" data-testid="portal-order-btn">
          <ShoppingCart className="mr-2 h-5 w-5" />Plätze bestellen
        </Button>
      </div>

      <div className="bg-white border border-slate-200/60 rounded-2xl overflow-hidden">
        {items === null ? (
          <div className="p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-cyan-700" /></div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-slate-500" data-testid="bestellungen-empty">
            <Receipt className="h-10 w-10 mx-auto mb-3 text-slate-300" />
            Noch keine Bestellung. Bestellen Sie Ihre ersten Schulungsplätze.
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {items.map((b) => (
              <div key={b.id} className="p-4 sm:p-5 flex flex-wrap items-center gap-4" data-testid={`bestellung-${b.id}`}>
                <div className="h-11 w-11 rounded-xl bg-slate-100 text-slate-700 flex items-center justify-center shrink-0"><Receipt className="h-5 w-5" /></div>
                <div className="flex-1 min-w-[180px]">
                  <p className="font-semibold text-slate-800">{b.anzahl} Schulungsplätze · {fmtEur(b.gesamt)}</p>
                  <p className="text-xs text-slate-500">Bestellt am {fmtDate(b.created_at)}{b.rechnungsnummer && ` · Rechnung ${b.rechnungsnummer} vom ${fmtDate(b.rechnungsdatum)}`}{b.bezahlt_am && ` · bezahlt am ${fmtDate(b.bezahlt_am)}`}</p>
                  {b.bemerkung && <p className="text-xs text-slate-400 mt-0.5">„{b.bemerkung}"</p>}
                </div>
                <StatusBadge map={BESTELL_STATUS} status={b.status} />
                {b.status === "offen" && (
                  <button onClick={() => storno(b)} className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-red-600" data-testid={`storno-${b.id}`}><XCircle className="h-4 w-4" />Stornieren</button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <BestellDialog open={open} onOpenChange={setOpen} onCreated={(b) => { setBestaetigung(b); load(); onChanged?.(); }} />
      <BestellBestaetigung bestellung={bestaetigung} onClose={() => setBestaetigung(null)} />
    </div>
  );
}
